import {
  DomainNotAllowedError,
  observeAuth,
  signInWithGoogle,
  signOutFacilitator,
} from "@/lib/firebase/auth"
import { rememberFacilitator } from "@/services/facilitators"
import { describeWriteError } from "@/utils/firestoreWrite"
import { useAuthStore } from "./authStore"
import { useSessionStore } from "./sessionStore"

/**
 * Wires Firebase Auth into the store, the same way `sessionSync` wires
 * Firestore:
 *
 *   Firebase Auth -> observer -> authStore -> React UI
 *
 * Nothing else writes auth state.
 */

let stop: (() => void) | null = null

/**
 * Starts watching who is signed in. Idempotent, and returns the teardown.
 *
 * Called once from the app root rather than from a route, because every screen's
 * first question is whether the app is still deciding — a guard that mounted the
 * observer itself would answer "signed out" on its first render, every time.
 */
export function startAuth(): () => void {
  if (stop) return stop

  const unsubscribe = observeAuth(
    (user) => {
      useAuthStore.getState().applyUser(user)
      // Mirrored into the session store so selectors can answer "am I the
      // facilitator of THIS session" from one store — see `signedInUid`.
      useSessionStore.getState().setSignedInUid(user?.uid ?? null)

      // Fire-and-forget: the profile only exists so a shared customer can render
      // a colleague's name, and failing to refresh it must not block sign-in.
      if (user) {
        void rememberFacilitator(user).catch((error: unknown) => {
          console.warn("Could not save the facilitator profile:", describeWriteError(error))
        })
      }
    },
    (rejection) => useAuthStore.getState().setError(rejection.message)
  )

  stop = () => {
    unsubscribe()
    stop = null
  }
  return stop
}

/**
 * Opens the Google account chooser.
 *
 * The result is not returned: the observer above is what updates the store, so
 * routing off "am I signed in" reacts to the same event whether the sign-in
 * happened here, in another tab, or was restored from persistence.
 */
export async function signIn(): Promise<void> {
  const store = useAuthStore.getState()
  if (store.pending) return

  store.setPending(true)
  store.setError(null)
  try {
    await signInWithGoogle()
  } catch (caught: unknown) {
    store.setPending(false)
    store.setError(describeSignInError(caught))
  }
}

export async function signOut(): Promise<void> {
  await signOutFacilitator()
  useAuthStore.getState().setError(null)
}

/**
 * Popup flows fail in ordinary ways that are not errors to apologise for —
 * closing the window is the usual one — so they are worded as what happened
 * rather than surfaced as a raw Firebase code.
 */
function describeSignInError(error: unknown): string {
  if (error instanceof DomainNotAllowedError) return error.message

  const code =
    error && typeof error === "object" && "code" in error
      ? String((error as { code: unknown }).code)
      : ""

  switch (code) {
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Sign-in was cancelled."
    case "auth/popup-blocked":
      return "The browser blocked the sign-in window. Allow pop-ups for this site and try again."
    case "auth/network-request-failed":
      return "Could not reach Google. Check the connection and try again."
    case "auth/unauthorized-domain":
      return "This address is not on the Firebase project's authorised domains."
    default:
      if (error instanceof Error && error.message) return error.message
      return "Sign-in did not complete."
  }
}
